import { ITranslateProps, InjectedFormProps } from 'models/common';
import * as React from 'react';
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
const { connect } = require('react-redux');
import * as c from 'models/constants';
import { reduxForm, Field } from 'redux-form';
import { PortalPageSection } from 'components/PortalStructure';
import {
	postShopSubscriptionPlan,
	putShopSubscriptionPlan,
} from 'redux/modules/subscriptionplans/shopsubscriptionplans';
import { IShopSubscriptionPlan, IShopSubscriptionPlanPost, IShopSubscriptionPlanPut } from 'models/subscriptionplans';
import { BookingDatePicker, BookingInputField, BookingSelectMenu } from 'components/FormControls';
import { getSubscriptionPlans } from 'redux/modules/subscriptionplans/subscriptionplans';
import { isNumber, required, requiredNumber } from 'helpers/validationattributes';
import { Button, WhiteButton } from 'components';
import moment = require('moment');
import { parseFields } from '../helpers';

require('./style.scss');

interface IOwnProps {
	shopId: string;
	shopSubscriptionPlan?: IShopSubscriptionPlan;
	isNewSubscription?: boolean;

	onCancelNew?: () => void;
	onFinish?: () => void;
}

type FormData = {
	[key: string]: Partial<IShopSubscriptionPlanPost & IShopSubscriptionPlanPut>;
};

interface IProps
	extends InjectedFormProps<FormData>,
		IOwnProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		ITranslateProps {}

interface IState {
	isEditMode: boolean;
}

const mapStateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: FormData = null;
	if (
		state.form[c.FORM_NAME_EDIT_SHOP_SUBSCRIPTION_PLAN] &&
		state.form[c.FORM_NAME_EDIT_SHOP_SUBSCRIPTION_PLAN].values
	) {
		currentFormValues = state.form[c.FORM_NAME_EDIT_SHOP_SUBSCRIPTION_PLAN].values;
	}
	const initialValues: FormData = { ...currentFormValues };
	if (ownProps.isNewSubscription) {
		if (ownProps.shopSubscriptionPlan) {
			initialValues['new'] = Object.assign({}, ownProps.shopSubscriptionPlan);
			initialValues['new'].subscriptionPlanId = ownProps.shopSubscriptionPlan?.subscriptionPlan?.id;
			initialValues['new'].validFrom = moment(ownProps.shopSubscriptionPlan.validTo)
				.add(1, 'day')
				.startOf('day')
				.toDate();
			initialValues['new'].validTo = undefined;
		} else {
			initialValues['new'] = {};
		}
	} else {
		initialValues[ownProps.shopSubscriptionPlan?.id] = ownProps.shopSubscriptionPlan;
		initialValues[ownProps.shopSubscriptionPlan?.id].subscriptionPlanId =
			ownProps.shopSubscriptionPlan?.subscriptionPlan?.id;
	}

	return {
		subscriptionPlans: state.subscriptionPlansReducer,
		initialValues: initialValues,
		currentFormValues,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getSubscriptionPlans: () => dispatch(getSubscriptionPlans()),
	postShopSubscriptionPlan: (plan) => dispatch(postShopSubscriptionPlan(plan)),
	putShopSubscripitonPlan: (plan) => dispatch(putShopSubscriptionPlan(plan)),
});

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class EditableShopSubscriptionForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			isEditMode: false,
		};

		this.onSubmit = this.onSubmit.bind(this);
	}

	componentDidMount(): void {
		if (!this.props.subscriptionPlans?.plans) {
			this.props.getSubscriptionPlans();
		}
	}

	componentDidUpdate(prevProps: Readonly<IProps>): void {
		// If you change the subscription plan, it should override freeSMSs field and freeEmails.
		const baseFormIndex = this.props.isNewSubscription ? 'new' : this.props.shopSubscriptionPlan?.id;

		const prevSPid = prevProps?.currentFormValues?.[baseFormIndex]?.subscriptionPlanId;
		const newSPid = this.props?.currentFormValues?.[baseFormIndex]?.subscriptionPlanId;

		if (prevSPid != newSPid) {
			const selectedSubscriptionPlan =
				newSPid && this.props.subscriptionPlans?.plans?.find((plan) => plan.id == newSPid);

			// If the subscription plan was only loaded and an existing ssp is being modified, do not override
			if (selectedSubscriptionPlan && !(!prevSPid && newSPid && this.props.shopSubscriptionPlan)) {
				this.props.change(`${baseFormIndex}.freeSmss`, selectedSubscriptionPlan.freeSmss);
				this.props.change(`${baseFormIndex}.freeEmails`, selectedSubscriptionPlan.freeEmails);
			}
		}
	}

	public onSubmit(form: FormData) {
		const { isNewSubscription, shopSubscriptionPlan } = this.props;
		const baseFormIndex = isNewSubscription ? 'new' : shopSubscriptionPlan?.id;

		const planData = parseFields({ ...form?.[baseFormIndex], shopId: this.props.shopId }, [
			['freeSmss', 'int'],
			['freeEmails', 'int'],
		]);

		planData.validFrom = moment(planData.validFrom).startOf('day').set('milliseconds', 0).toISOString();
		planData.validTo = moment(planData.validTo).endOf('day').set('milliseconds', 0).toISOString();

		let postPutPromise;

		if (!this.props.isNewSubscription) {
			(planData as IShopSubscriptionPlanPut).id = shopSubscriptionPlan.id;
			postPutPromise = this.props.putShopSubscripitonPlan(planData);
		} else {
			postPutPromise = this.props.postShopSubscriptionPlan(planData);
		}

		postPutPromise.then(() => {
			this.setState({ isEditMode: false });
			this.props.onFinish && this.props.onFinish();
		});
	}

	public render() {
		const { isNewSubscription, shopSubscriptionPlan, t } = this.props;
		const baseFormIndex = isNewSubscription ? 'new' : shopSubscriptionPlan?.id;
		const subscriptionPlansItems =
			this.props.subscriptionPlans?.plans?.map((plan) => ({
				key: plan.id,
				value: plan.name,
			})) || [];

		const isCurrentlyActive =
			!this.state.isEditMode && !isNewSubscription && shopSubscriptionPlan
				? moment(shopSubscriptionPlan.validFrom).unix() < moment().unix() &&
				  moment().unix() <= moment(shopSubscriptionPlan.validTo).unix()
				: false;

		const selectedPlan = this.props.subscriptionPlans?.plans?.find(
			(p) => p?.id == this.props.currentFormValues?.[baseFormIndex]?.subscriptionPlanId,
		);

		return (
			<PortalPageSection
				toggleEdit={
					!isNewSubscription
						? () => {
								this.setState((state) => ({
									isEditMode: !state.isEditMode,
								}));
						  }
						: undefined
				}
				readOnly={!this.state.isEditMode && !isNewSubscription}
				className="editable-shop-subscription"
				variant={isCurrentlyActive ? 'active' : 'normal'}
				title={this.props.shopSubscriptionPlan?.subscriptionPlan?.name}
			>
				{(isNewSubscription || this.state.isEditMode) && (
					<form onSubmit={this.props.handleSubmit(this.onSubmit)}>
						<Field
							name={`${baseFormIndex}.subscriptionPlanId`}
							component={BookingSelectMenu}
							items={subscriptionPlansItems}
							label={t('Admin_ShopSubscriptionPlans_Field_SubscriptionPlan')}
							information={t('Admin_ShopSubscriptionPlans_Field_SubscriptionPlan')}
						/>
						<Field
							name={`${baseFormIndex}.validFrom`}
							component={BookingDatePicker}
							type="text"
							label={t('Admin_ShopSubscriptionPlans_Field_ValidFrom')}
							information={t('Admin_ShopSubscriptionPlans_Field_ValidFrom')}
							validate={[required]}
						/>
						<Field
							name={`${baseFormIndex}.validTo`}
							component={BookingDatePicker}
							minDate={
								this.props.currentFormValues?.[baseFormIndex]?.validFrom &&
								new Date(this.props.currentFormValues?.[baseFormIndex]?.validFrom)
							}
							type="text"
							label={t('Admin_ShopSubscriptionPlans_Field_ValidTo')}
							information={t('Admin_ShopSubscriptionPlans_Field_ValidTo')}
							validate={[required]}
						/>

						<Field
							name={`${baseFormIndex}.freeSmss`}
							component={BookingInputField}
							placeholder={''}
							information={t('Admin_NewSubscriptionPlan_FreeSmss')}
							validate={[requiredNumber, isNumber]}
						/>

						{selectedPlan && (
							<p>
								{t('Admin_EditShopSubscriptionPlans_BasePlanIncludes')} {selectedPlan.freeSmss}
							</p>
						)}

						<Field
							name={`${baseFormIndex}.freeEmails`}
							component={BookingInputField}
							placeholder={''}
							information={t('Admin_NewSubscriptionPlan_FreeEmails')}
							validate={[isNumber]}
						/>

						{selectedPlan && (
							<p>
								{t('Admin_EditShopSubscriptionPlans_BasePlanIncludes')} {selectedPlan.freeEmails}
							</p>
						)}

						<div className="editable-shop-subscription__bottom-buttons">
							{this.props.isNewSubscription && (
								<WhiteButton
									onClick={() => {
										this.setState({ isEditMode: false });
										if (this.props.onCancelNew) {
											this.props.onCancelNew();
										}
									}}
								>
									{t('BSPortal_Common_Cancel')}
								</WhiteButton>
							)}
							<Button type="submit">{t('BSPortal_Common_Save')}</Button>
						</div>
					</form>
				)}
				{shopSubscriptionPlan && !this.state.isEditMode && !isNewSubscription && (
					<>
						<div className="editable-shop-subscription__info-title">{t('Admin_NewSubscriptionPlan_FreeSmss')}</div>
						<p className="editable-shop-subscription__info-value">{shopSubscriptionPlan.freeSmss}</p>
						<div className="editable-shop-subscription__info-title">{t('Admin_NewSubscriptionPlan_FreeEmails')}</div>
						<p className="editable-shop-subscription__info-value">{shopSubscriptionPlan.freeEmails}</p>
						<div className="editable-shop-subscription__info-title">
							{t('Admin_ShopSubscriptionPlans_Field_ValidFrom')}
						</div>
						<p className="editable-shop-subscription__info-value">
							{moment(shopSubscriptionPlan.validFrom).format('ll')}
						</p>
						<div className="editable-shop-subscription__info-title">
							{t('Admin_ShopSubscriptionPlans_Field_ValidTo')}
						</div>
						<p className="editable-shop-subscription__info-value">
							{moment(shopSubscriptionPlan.validTo).format('ll')}{' '}
							{isCurrentlyActive && (
								<span>({moment.duration(moment(shopSubscriptionPlan.validTo).diff(moment())).humanize(true)})</span>
							)}
						</p>
					</>
				)}
			</PortalPageSection>
		);
	}
}

let EditableShopSubscription = reduxForm({
	form: c.FORM_NAME_EDIT_SHOP_SUBSCRIPTION_PLAN,
	enableReinitialize: true,
})(EditableShopSubscriptionForm);

EditableShopSubscription = connect(mapStateToProps, mapDispatchToProps)(EditableShopSubscription);

export { EditableShopSubscription };
